import React, { useEffect, useMemo, useState } from "react";

import { Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useNavigate, Navigate } from "react-router-dom";

import Error from "Components/Error";
import PageMeta from "Components/PageMeta";
import { BackLink, Button, ButtonWrapper } from "ds/Button";
import Loading from "ds/PageLoading";
import { Tooltip } from "ds/Tooltip";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { capitalize } from "Libs/utils";
import { environmentsAsArraySelector } from "Reducers/environment";
import { projectCapabilitySelector } from "Reducers/project/capabilities";
import { getIsLoadingProjects } from "Reducers/project/project";
import { useLoadDomain } from "Reducers/project/settings/domains/hooks/useLoadDomain";
import ModalConfirmDelete from "src/organization/pages/project/settings/domains/components/DeleteModal";
import { useAppSelector } from "Store/hooks";

import * as S from "./DomainItem.styles";
//import { useLoadDomains } from "Reducers/project/settings/domains/hooks/useLoadDomains";

import type { Project } from "@packages/client";

interface DomainItemProps {
  project?: Project;
}
const DomainItem = ({ project }: DomainItemProps) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { organizationId, projectId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
  }>();

  const [modal, setModal] = useState<{
    isOpen: boolean;
    type?: string;
  }>({
    isOpen: false
  });

  const environments = useSelectorWithUrlParams(environmentsAsArraySelector);

  const capabilites = useAppSelector(state =>
    projectCapabilitySelector(state, { projectId, organizationId })
  );

  const { domain, loading, errors, status, handleDelete, handleSetDefault } =
    useLoadDomain(project);

  const isProjectLoading = useAppSelector(getIsLoadingProjects);

  const canEdit = domain?.hasLink("#edit");
  const canDelete = domain?.hasLink("#delete");

  const capabilityCustomDomains = capabilites?.custom_domains?.enabled;

  useEffect(() => {
    if (status && ["deleted"].includes(status)) navigate(listDomainsUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const listDomainsUrl = useMemo(
    () => `/${organizationId}/${projectId}/-/settings/domains`,
    [organizationId, projectId]
  );

  const mainEnvironment = useMemo(
    () => environments.find(environment => environment.is_main),
    [environments]
  );

  const isDefaultDomain = project?.default_domain === domain?.name;

  if (!mainEnvironment) return null;
  if (!domain && loading === false) return <Navigate to={listDomainsUrl} />;

  const makeDefaultBtn = (
    <Button
      analyticId="settings.domains.make_default"
      onClick={() => {
        if (domain?.id) {
          handleSetDefault(domain.id);
        }
      }}
      disabled={!canEdit}
    >
      {capitalize(
        intl.formatMessage({
          id: "settings.domains.make_default"
        })
      )}
    </Button>
  );

  const makeDefaultComponent = !canEdit ? (
    <Tooltip
      align="end"
      tooltip={intl.formatMessage({
        id: "no_access.project.make_default_domain"
      })}
      analyticId="no_access.project.make_default_domain"
    >
      {makeDefaultBtn}
    </Tooltip>
  ) : (
    makeDefaultBtn
  );

  const deleteBtn = (
    <Button
      variant="outline"
      onClick={() => {
        setModal({ isOpen: true, type: "delete" });
      }}
      data-testid="delete-domain-btn"
      disabled={!canDelete}
      analyticId="settings.domains.delete"
    >
      {capitalize(intl.formatMessage({ id: "settings.domains.delete" }))}
    </Button>
  );

  const deleteComponent = !canDelete ? (
    <Tooltip
      align="end"
      tooltip={intl.formatMessage({
        id: "no_access.project.delete_domain"
      })}
      analyticId="no_access.project.delete_domain"
    >
      {deleteBtn}
    </Tooltip>
  ) : (
    deleteBtn
  );

  return (
    <div>
      <PageMeta title={`Domains | ${project?.title}`} />
      <section aria-labelledby="settings-heading">
        {!domain && loading ? (
          <Loading />
        ) : (
          <>
            <BackLink from="domains" to={listDomainsUrl} />
            <S.Title tag="h2" style={{ marginBottom: "16px" }}>
              {domain?.name}
              {project?.default_domain === domain?.name && (
                <S.Default>
                  {intl.formatMessage({
                    id: "settings.domains.default"
                  })}
                </S.Default>
              )}
            </S.Title>

            <S.FormWrapper aria-labelledby="edit-domain-heading">
              {errors && <Error>{errors.detail.error}</Error>}

              <strong>{intl.formatMessage({ id: "settings.details" })}</strong>

              <S.EnvironmentContainer>
                <Typography.Label>
                  {capitalize(
                    intl.formatMessage({ id: "settings.domains.name" })
                  )}
                </Typography.Label>
                <S.TagContainer>
                  <S.Text>{domain?.name}</S.Text>

                  {isDefaultDomain && (
                    <S.Default>
                      {intl.formatMessage({
                        id: "settings.domains.default"
                      })}
                    </S.Default>
                  )}
                </S.TagContainer>
              </S.EnvironmentContainer>

              <S.EnvironmentContainer>
                <Typography.Label>
                  {capitalize(
                    intl.formatMessage({ id: "domains.environment" })
                  )}
                </Typography.Label>
                <S.Text>{mainEnvironment.id}</S.Text>
              </S.EnvironmentContainer>

              <ButtonWrapper>
                {status === "pending" || isProjectLoading ? (
                  <Loading />
                ) : (
                  <S.ButtonContainer>
                    {project?.default_domain !== domain?.name &&
                      makeDefaultComponent}

                    <S.DeleteButtonWrapper>
                      {deleteComponent}
                    </S.DeleteButtonWrapper>
                  </S.ButtonContainer>
                )}
              </ButtonWrapper>
            </S.FormWrapper>
          </>
        )}

        {modal.isOpen && domain && (
          <ModalConfirmDelete
            closeModal={() => {
              setModal({ isOpen: false });
            }}
            deleteProductionDomain={handleDelete}
            domain={domain}
            isOpen={modal.isOpen}
            project={project}
            capabilityCustomDomains={capabilityCustomDomains}
          />
        )}
      </section>
    </div>
  );
};

export default DomainItem;
